import { useEffect, useState } from "react";
import { isNativePlatform, loadNativeGeolocation } from "@/lib/native-geolocation";

/**
 * Acompanha o estado da permissão de geolocation.
 * - No app nativo (iOS/Android via Capacitor): consulta o plugin nativo,
 *   que suporta permissão "always" (background) usada pelo modo "Perto de mim".
 * - Na web: consulta a Permissions API.
 * Retorna "unknown" quando indisponível.
 */
export const useGeolocationPermission = (enabled: boolean) => {
  const [permissionState, setPermissionState] = useState<PermissionState | "unknown">(
    "unknown"
  );

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const checkNative = async () => {
      const Geolocation = await loadNativeGeolocation();
      if (!Geolocation || cancelled) {
        setPermissionState("unknown");
        return;
      }
      try {
        const perm = await Geolocation.checkPermissions();
        if (cancelled) return;
        const state: PermissionState =
          perm.location === "granted"
            ? "granted"
            : perm.location === "denied"
              ? "denied"
              : "prompt";
        setPermissionState(state);
      } catch {
        setPermissionState("unknown");
      }
    };

    const checkWeb = async () => {
      if (typeof navigator === "undefined" || !navigator.geolocation) return;
      if (!("permissions" in navigator)) {
        setPermissionState("unknown");
        return;
      }
      try {
        const status = await navigator.permissions.query({
          name: "geolocation" as PermissionName,
        });
        if (cancelled) return;
        setPermissionState(status.state);
        status.onchange = () => setPermissionState(status.state);
      } catch {
        setPermissionState("unknown");
      }
    };

    if (isNativePlatform()) {
      void checkNative();
    } else {
      void checkWeb();
    }

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return [permissionState, setPermissionState] as const;
};
